import {KeyedAction} from '../Store';
import {RootState} from '../State';
import {Point, DirectionalPosition, LauncherSize} from '../../types/commons';

export interface SettingsData {
    version: number;
    pinnedAppIds: string[];
    launcherMonitorId: string | number | null;
    launcherMonitorReferencePoint: Point;
    launcherPosition: DirectionalPosition;
    launcherSize: LauncherSize;
    launcherDocked: boolean;
    themeId?: string;
}

export type SettingsState = Readonly<SettingsData>;

export const SettingsInitialState: SettingsState = {
    version: 1,
    pinnedAppIds: [],
    launcherMonitorId: null,
    launcherMonitorReferencePoint: {x: 0, y: 0},
    launcherPosition: DirectionalPosition.Top,
    launcherSize: LauncherSize.Large,
    launcherDocked: true
};

/**
 * Perform an update of the user settings.
 */
export class UpdateSettings extends KeyedAction<RootState, 'settings'> {
    public readonly type: string = '@@SETTINGS/UPDATE_SETTINGS';

    constructor(public readonly settings: SettingsData) {
        super('settings');
    }

    public reduce(state: SettingsState): SettingsState {
        const settingsState: SettingsState = this.settings;
        return {...state, ...settingsState};
    }
}

export class SetThemeId extends KeyedAction<RootState, 'settings'> {
    public readonly type: string = '@@SETTINGS/SET_THEME_ID';

    constructor(public readonly themeId: string) {
        super('settings');
    }

    public reduce(state: SettingsState): SettingsState {
        const {themeId} = this;
        return {
            ...state,
            themeId
        };
    }
}

/**
 * Store an application in settings as pinned.
 */
export class PinApplication extends KeyedAction<RootState, 'settings'> {
    public readonly type: string = '@@SETTINGS/PIN_APPLICATION';

    constructor(private readonly appId: string) {
        super('settings');
    }

    public reduce(state: SettingsState): SettingsState {
        return state.pinnedAppIds.includes(this.appId) ? state : {...state, pinnedAppIds: [...state.pinnedAppIds, this.appId]};
    }
}

/**
 * Remove a pinned application from settings.
 */
export class UnpinApplication extends KeyedAction<RootState, 'settings'> {
    public readonly type: string = '@@SETTINGS/UNPIN_APPLICATION';

    constructor(private readonly appId: string) {
        super('settings');
    }

    public reduce(state: SettingsState): SettingsState {
        return state.pinnedAppIds.includes(this.appId) ? {...state, pinnedAppIds: state.pinnedAppIds.filter((id) => id !== this.appId)} : state;
    }
}

export class SetLaunchBarDocked extends KeyedAction<RootState, 'settings'> {
    public readonly type: string = '@@SETTINGS/SET_DOCKED';

    constructor(public readonly docked: boolean) {
        super('settings');
    }

    public reduce(state: SettingsState): SettingsState {
        const {docked} = this;
        return {
            ...state,
            launcherDocked: docked
        };
    }
}

export const getPinnedAppIds = (state: RootState) => state.settings.pinnedAppIds;
export const isPinnedApp = (state: RootState, appId: string) => getPinnedAppIds(state).includes(`${appId}`);
